import { Component, signal, inject, computed, OnInit, ViewChild, ViewContainerRef, ComponentRef, effect } from '@angular/core';
import { Router, RouterOutlet, RouterModule, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from './services/auth.service';
import { PushNotificationService } from './services/push-notification.service';
import { filter } from 'rxjs/operators';

/**
 * Root Component – Sidebar-Navigation
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterModule],
  template: `
    <div class="app-container" [class.sidebar-open]="menuOpen()">

      <!-- Mobile Top-Bar -->
      <header class="mobile-topbar">
        <button class="hamburger" (click)="toggleMenu()" [attr.aria-label]="menuOpen() ? 'Menü schließen' : 'Menü öffnen'">
          <span></span><span></span><span></span>
        </button>
        <a routerLink="/" class="topbar-brand">SM Display</a>
      </header>

      <main class="app-main">
        <!-- ViewAs-Banner -->
        @if (authService.isViewingAs()) {
          <div class="viewas-banner">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;flex-shrink:0"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            Vorschau: Ansicht als <strong>{{ viewAsLabel() }}</strong>
            <button class="viewas-exit" (click)="clearViewAs()">Vorschau beenden</button>
          </div>
        }
        @if (showPushPrompt()) {
          <div class="push-prompt-banner" role="region" aria-label="Push-Benachrichtigungen">
            <div class="push-prompt-text">
              <strong>Push-Benachrichtigungen</strong>
              <span>Termine, Verträge und Installationen direkt im Browser.</span>
              @if (pushPromptError()) {
                <span class="push-prompt-error">{{ pushPromptError() }}</span>
              }
            </div>
            <div class="push-prompt-actions">
              <button type="button" class="push-prompt-enable" (click)="enablePushNotifications()" [disabled]="pushPromptLoading()">
                {{ pushPromptLoading() ? '…' : 'Aktivieren' }}
              </button>
              <button type="button" class="push-prompt-dismiss" (click)="dismissPushPrompt()">Später</button>
            </div>
          </div>
        }
        <router-outlet></router-outlet>
      </main>

      <!-- Navigation nach Hauptinhalt (Leaflet-GPU-Layer sonst über Sidebar) -->
      <div class="nav-layer">
        <div class="sidebar-overlay" (click)="closeMenu()"></div>
        <aside class="sidebar">
        <div class="sidebar-brand-wrap">
          <a routerLink="/" class="sidebar-brand" (click)="closeMenu()">SM Display</a>
        </div>

        <nav class="sidebar-nav">
          @if (authService.isLoggedIn()) {
            @if (authService.canUseVoiceAssistant()) {
              <button class="nav-item nav-item-voice" (click)="openVoiceAssistant()">
                <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                Sprachassistent
              </button>
            }
            @if (authService.isKunde()) {
              <a routerLink="/customer" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                Meine Dateien
              </a>
            } @else if (authService.isInstaller()) {
              <a routerLink="/installer" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                Meine Aufträge
              </a>
            } @else {
              <div class="nav-section-label">Betrieb</div>
              @if (authService.canViewExecutiveDashboard()) {
                <a routerLink="/overview" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M7 16l4-8 4 5 5-9"/></svg>
                  Übersicht
                </a>
              }
              <a routerLink="/devices" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                Dashboard
              </a>
              <a routerLink="/bestand" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                Bestand
              </a>
              <a routerLink="/requests" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                Anfragen
              </a>
              <a routerLink="/display-setup" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                Display-Einrichtung
              </a>

              @if (authService.canManageCustomers() || authService.canManageLeads()) {
                <div class="nav-section-label">Vertrieb</div>
              }
              @if (authService.canManageLeads()) {
                <a routerLink="/leads" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                  Leads
                </a>
                <a routerLink="/sales-proposals" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                  Angebote
                </a>
              }
              @if (authService.canManageCustomers()) {
                <a routerLink="/appointments" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  Termine
                </a>
                <a routerLink="/customers" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  Kunden
                </a>
                <a routerLink="/location-partners" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  Standort-Partner
                </a>
                <a
                  routerLink="/installation"
                  [queryParams]="{ tab: 'jobs' }"
                  [class.active]="installationNavActive()"
                  class="nav-item"
                  (click)="closeMenu()"
                >
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                  Installation
                </a>
                <a routerLink="/price-packages" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                  Preispakete
                </a>
                <a routerLink="/contracts" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                  Verträge
                </a>
              }

              @if (authService.isAdmin()) {
                <div class="nav-section-label">System</div>
                <a routerLink="/admin" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  Admin
                </a>
                <a routerLink="/users" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  Benutzer
                </a>
                <a routerLink="/regions" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
                  Regionen
                </a>
              }
            }
          } @else {
            <a routerLink="/" [queryParams]="{ login: 'true' }" queryParamsHandling="merge" class="nav-item" (click)="closeMenu()">
              <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
              Anmelden
            </a>
          }
        </nav>

        <!-- Bottom: Ansicht (Admin) + Profil + Abmelden -->
        @if (authService.isLoggedIn()) {
          <div class="sidebar-bottom">
            @if (authService.isRealAdmin()) {
              <button class="nav-item nav-item-view" [class.nav-item-view-active]="authService.isViewingAs()" (click)="openViewAs()">
                <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                Ansicht
                @if (authService.isViewingAs()) {
                  <span class="nav-view-badge">{{ viewAsLabel() }}</span>
                }
              </button>
            }
            <a routerLink="/profile" routerLinkActive="active" class="nav-item" (click)="closeMenu()">
              <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              Profil
            </a>
            <button class="nav-item nav-logout" (click)="logout()">
              <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Abmelden
            </button>
          </div>
        }
      </aside>
      </div>
    </div>

    <ng-container #voiceAssistantHost></ng-container>

    <!-- ViewAs-Modal -->
    @if (showViewAsModal()) {
      <div class="modal-backdrop" (click)="closeViewAs()">
        <div class="viewas-modal" (click)="$event.stopPropagation()">
          <div class="viewas-modal-header">
            <h3>App-Ansicht wählen</h3>
            <button class="modal-close" (click)="closeViewAs()">&times;</button>
          </div>
          <p class="viewas-hint">Wählen Sie einen Benutzertyp, um zu sehen, wie die App für diese Rolle aussieht. Ihre eigenen Berechtigungen bleiben erhalten.</p>
          <div class="viewas-roles">
            @for (role of viewAsRoles; track role.value) {
              <button class="viewas-role-btn" [class.viewas-role-selected]="authService.isViewingAs() === role.value" (click)="selectViewAs(role.value)">
                <span class="viewas-role-icon" [innerHTML]="role.icon"></span>
                <div>
                  <div class="viewas-role-name">{{ role.label }}</div>
                  <div class="viewas-role-desc">{{ role.desc }}</div>
                </div>
              </button>
            }
          </div>
          @if (authService.isViewingAs()) {
            <div class="viewas-modal-footer">
              <button class="btn-viewas-exit" (click)="clearViewAs()">Vorschau beenden &amp; Admin-Ansicht wiederherstellen</button>
            </div>
          }
        </div>
      </div>
    }
  `,
  styles: [`
    /* ── Layout ─────────────────────────────────────────── */
    :host { display: block; }

    .app-container {
      display: flex;
      min-height: 100vh;
    }

    /* Navigation über Seiteninhalt (nach main im DOM, eigener Compositing-Layer) */
    .nav-layer {
      position: fixed;
      inset: 0;
      z-index: var(--z-nav-overlay);
      pointer-events: none;
    }

    .nav-layer .sidebar {
      pointer-events: auto;
    }

    /* ── Sidebar ─────────────────────────────────────────── */
    .sidebar {
      width: 220px;
      flex-shrink: 0;
      background: #1e293b;
      display: flex;
      flex-direction: column;
      position: fixed;
      inset: 0 auto 0 0;
      z-index: var(--z-nav-sidebar);
      overflow-y: auto;
      transform: translateZ(0);
    }

    .sidebar-brand-wrap {
      padding: 1.25rem 1.25rem 1rem;
      border-bottom: 1px solid rgba(255,255,255,0.08);
    }

    .sidebar-brand {
      font-size: 1.125rem;
      font-weight: 700;
      color: #fff;
      text-decoration: none;
      letter-spacing: 0.02em;
    }

    /* ── Nav items ───────────────────────────────────────── */
    .sidebar-nav {
      flex: 1;
      padding: 0.75rem 0.75rem 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .nav-section-label {
      font-size: 0.6875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #64748b;
      padding: 1rem 0.5rem 0.25rem;
      margin-top: 0.25rem;
    }

    .nav-section-label:first-child {
      padding-top: 0.25rem;
    }

    .nav-item {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      padding: 0.55rem 0.75rem;
      border-radius: 6px;
      color: #94a3b8;
      text-decoration: none;
      font-size: 0.9rem;
      font-weight: 500;
      transition: background 0.15s, color 0.15s;
      border: none;
      background: transparent;
      cursor: pointer;
      width: 100%;
      text-align: left;
    }

    .nav-item:hover {
      background: rgba(255,255,255,0.07);
      color: #e2e8f0;
    }

    .nav-item.active {
      background: rgba(255,255,255,0.12);
      color: #fff;
    }

    .nav-icon {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
      opacity: 0.85;
    }

    .nav-item.active .nav-icon,
    .nav-item:hover .nav-icon {
      opacity: 1;
    }

    /* ── Bottom section ──────────────────────────────────── */
    .sidebar-bottom {
      padding: 0.75rem;
      border-top: 1px solid rgba(255,255,255,0.08);
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .nav-logout {
      color: #f87171;
    }

    .nav-logout:hover {
      background: rgba(239,68,68,0.12);
      color: #fca5a5;
    }

    /* ── Ansicht-Button ──────────────────────────────────── */
    .nav-item-view {
      color: #94a3b8;
      position: relative;
    }
    .nav-item-view:hover {
      background: rgba(255,255,255,0.07);
      color: #e2e8f0;
    }
    .nav-item-view-active {
      color: #fbbf24 !important;
    }
    .nav-item-view-active:hover {
      background: rgba(251,191,36,0.1) !important;
    }
    .nav-item-voice {
      color: #93c5fd;
    }
    .nav-item-voice:hover {
      background: rgba(59,130,246,0.12);
      color: #bfdbfe;
    }
    .nav-view-badge {
      margin-left: auto;
      font-size: 0.65rem;
      background: #fbbf24;
      color: #1e293b;
      border-radius: 99px;
      padding: 0.1rem 0.45rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      white-space: nowrap;
    }

    /* ── ViewAs-Banner ───────────────────────────────────── */
    .viewas-banner {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      background: #fefce8;
      border: 1px solid #fde047;
      border-radius: 8px;
      padding: 0.6rem 1rem;
      margin-bottom: 1.25rem;
      font-size: 0.875rem;
      color: #713f12;
    }
    .viewas-exit {
      margin-left: auto;
      background: #fde047;
      border: none;
      border-radius: 5px;
      padding: 0.3rem 0.75rem;
      font-size: 0.8125rem;
      font-weight: 600;
      color: #713f12;
      cursor: pointer;
      white-space: nowrap;
    }
    .viewas-exit:hover { background: #facc15; }

    .push-prompt-banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      flex-wrap: wrap;
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
      padding: 0.75rem 1rem;
      margin-bottom: 1.25rem;
      font-size: 0.875rem;
      color: #1e3a8a;
    }

    .push-prompt-text {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
      min-width: 0;
    }

    .push-prompt-text strong { font-weight: 600; }

    .push-prompt-error {
      color: #b91c1c;
      font-size: 0.8125rem;
    }

    .push-prompt-actions {
      display: flex;
      gap: 0.5rem;
      flex-shrink: 0;
    }

    .push-prompt-enable {
      background: #2563eb;
      color: white;
      border: none;
      border-radius: 6px;
      padding: 0.45rem 0.9rem;
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
    }

    .push-prompt-enable:disabled {
      opacity: 0.7;
      cursor: wait;
    }

    .push-prompt-dismiss {
      background: transparent;
      color: #475569;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      padding: 0.45rem 0.9rem;
      font-size: 0.875rem;
      cursor: pointer;
    }

    /* ── ViewAs-Modal ────────────────────────────────────── */
    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: var(--z-app-modal);
      padding: 1rem;
    }
    .viewas-modal {
      background: #fff;
      border-radius: 12px;
      padding: 1.5rem;
      width: 100%;
      max-width: 440px;
      box-shadow: 0 12px 40px rgba(0,0,0,0.18);
    }
    .viewas-modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }
    .viewas-modal-header h3 { margin: 0; font-size: 1.1rem; color: #1e293b; }
    .modal-close {
      background: none; border: none; font-size: 1.5rem;
      cursor: pointer; color: #64748b; line-height: 1;
    }
    .modal-close:hover { color: #1e293b; }
    .viewas-hint {
      font-size: 0.8125rem;
      color: #64748b;
      margin: 0 0 1rem;
      line-height: 1.5;
    }
    .viewas-roles {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    .viewas-role-btn {
      display: flex;
      align-items: center;
      gap: 0.875rem;
      padding: 0.75rem 1rem;
      border: 1.5px solid #e2e8f0;
      border-radius: 8px;
      background: #fff;
      cursor: pointer;
      text-align: left;
      transition: border-color 0.15s, background 0.15s;
      width: 100%;
    }
    .viewas-role-btn:hover { background: #f8fafc; border-color: #cbd5e1; }
    .viewas-role-selected { border-color: #2563eb !important; background: #eff6ff !important; }
    .viewas-role-icon { font-size: 1.25rem; flex-shrink: 0; }
    .viewas-role-name { font-weight: 600; font-size: 0.9rem; color: #1e293b; }
    .viewas-role-desc { font-size: 0.775rem; color: #64748b; margin-top: 0.1rem; }
    .viewas-modal-footer {
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px solid #f1f5f9;
    }
    .btn-viewas-exit {
      width: 100%;
      padding: 0.55rem 1rem;
      background: #fef9c3;
      border: 1px solid #fde047;
      border-radius: 7px;
      color: #713f12;
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
    }
    .btn-viewas-exit:hover { background: #fef08a; }

    /* ── Main content ────────────────────────────────────── */
    .app-main {
      flex: 1;
      margin-left: 220px;
      padding: 2rem;
      min-height: 100vh;
      background: #f8fafc;
      max-width: calc(100vw - 220px);
      min-width: 0;
    }

    /* ── Mobile top bar (hidden on desktop) ──────────────── */
    .mobile-topbar {
      display: none;
    }

    /* ── Overlay (hidden on desktop) ─────────────────────── */
    .sidebar-overlay {
      display: none;
      position: fixed;
      inset: 0;
      z-index: var(--z-nav-overlay);
    }

    /* ── Mobile ──────────────────────────────────────────── */
    @media (max-width: 768px) {
      .app-container {
        flex-direction: column;
      }

      /* Mobile top bar */
      .mobile-topbar {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        background: #1e293b;
        padding: 0.875rem 1rem;
        position: sticky;
        top: 0;
        z-index: var(--z-nav-topbar);
      }

      .topbar-brand {
        font-size: 1.1rem;
        font-weight: 700;
        color: #fff;
        text-decoration: none;
      }

      /* Hamburger */
      .hamburger {
        display: flex;
        flex-direction: column;
        gap: 5px;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 0.25rem;
        flex-shrink: 0;
      }
      .hamburger span {
        display: block;
        width: 22px;
        height: 2px;
        background: #fff;
        border-radius: 2px;
        transition: transform 0.25s, opacity 0.25s;
      }
      .app-container.sidebar-open .hamburger span:nth-child(1) {
        transform: rotate(45deg) translate(5px, 5px);
      }
      .app-container.sidebar-open .hamburger span:nth-child(2) {
        opacity: 0;
      }
      .app-container.sidebar-open .hamburger span:nth-child(3) {
        transform: rotate(-45deg) translate(5px, -5px);
      }

      /* Sidebar: hidden off-screen left */
      .sidebar {
        width: 260px;
        transform: translate3d(-100%, 0, 0);
        transition: transform 0.28s ease;
        top: 0;
        padding-top: 3.25rem;
      }
      .app-container.sidebar-open .sidebar {
        transform: translate3d(0, 0, 0);
      }

      /* Overlay */
      .sidebar-overlay {
        display: block;
        background: rgba(0,0,0,0.45);
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.28s ease;
      }
      .app-container.sidebar-open .sidebar-overlay {
        opacity: 1;
        pointer-events: auto;
      }

      /* Hide desktop brand from sidebar on mobile (shown in topbar) */
      .sidebar-brand-wrap {
        display: none;
      }

      .app-main {
        margin-left: 0;
        padding: 1rem;
        max-width: none;
        width: 100%;
        min-width: 0;
        overflow-x: hidden;
        position: relative;
        z-index: var(--z-base);
      }
    }
  `],
})
export class AppComponent implements OnInit {
  readonly authService = inject(AuthService);
  private readonly pushNotificationService = inject(PushNotificationService);
  private readonly router = inject(Router);

  @ViewChild('voiceAssistantHost', { read: ViewContainerRef })
  private voiceAssistantHost?: ViewContainerRef;

  private voiceAssistantRef: ComponentRef<unknown> | null = null;

  title = 'SM Display Admin Dashboard';
  menuOpen = signal(false);
  showViewAsModal = signal(false);
  installationNavActive = signal(false);
  private readonly pushPromptRevision = signal(0);
  readonly showPushPrompt = computed(() => {
    this.pushPromptRevision();
    if (!this.authService.isLoggedIn()) return false;
    return this.pushNotificationService.shouldShowPushPrompt();
  });
  pushPromptLoading = signal(false);
  pushPromptError = signal<string | null>(null);

  constructor() {
    effect(() => {
      if (this.authService.isLoggedIn()) {
        void this.pushNotificationService.syncPushSubscriptionIfGranted();
      }
    });
  }

  private refreshPushPrompt(): void {
    this.pushPromptRevision.update((n) => n + 1);
  }

  ngOnInit(): void {
    this.syncInstallationNavActive();
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      this.syncInstallationNavActive();
    });
  }

  private syncInstallationNavActive(): void {
    const path = this.router.url.split('?')[0];
    this.installationNavActive.set(
      path === '/installation' || path.startsWith('/installers/') || path.startsWith('/installation-jobs/'),
    );
  }

  readonly viewAsRoles = [
    {
      value: 'manager',
      label: 'Manager',
      desc: 'Vollzugriff auf Betrieb und Vertrieb, kein Benutzermanagement',
      icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:20px;height:20px"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-4 0v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>',
    },
    {
      value: 'sales',
      label: 'Vertrieb (Sales)',
      desc: 'Kunden, Standort-Partner, Installation, Preispakete, Termine',
      icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:20px;height:20px"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
    },
    {
      value: 'support',
      label: 'Support',
      desc: 'Kunden und Geräte einsehen, kein Bearbeiten',
      icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:20px;height:20px"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
    },
    {
      value: 'installer',
      label: 'Installateur',
      desc: 'Installationsaufträge einsehen und bearbeiten',
      icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:20px;height:20px"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>',
    },
    {
      value: 'kunde',
      label: 'Kunde',
      desc: 'Nur eigene Dateien / Medienverwaltung sichtbar',
      icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:20px;height:20px"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
    },
  ];

  readonly viewAsLabel = computed(() => {
    const role = this.authService.isViewingAs();
    return this.viewAsRoles.find(r => r.value === role)?.label ?? role ?? '';
  });

  toggleMenu(): void {
    this.menuOpen.set(!this.menuOpen());
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

  logout(): void {
    this.closeMenu();
    this.authService.logout();
  }

  openViewAs(): void {
    this.showViewAsModal.set(true);
    this.closeMenu();
  }

  openVoiceAssistant(): void {
    this.closeMenu();
    void this.loadVoiceAssistantPanel();
  }

  closeVoiceAssistant(): void {
    this.voiceAssistantRef?.destroy();
    this.voiceAssistantRef = null;
  }

  private async loadVoiceAssistantPanel(): Promise<void> {
    if (this.voiceAssistantRef || !this.voiceAssistantHost) return;

    const { VoiceAssistantPanelComponent } = await import('./shared/voice-assistant-panel.component');
    if (this.voiceAssistantRef || !this.voiceAssistantHost) return;

    const ref = this.voiceAssistantHost.createComponent(VoiceAssistantPanelComponent);
    ref.instance.close.subscribe(() => this.closeVoiceAssistant());
    this.voiceAssistantRef = ref;
  }

  closeViewAs(): void {
    this.showViewAsModal.set(false);
  }

  selectViewAs(role: string): void {
    this.authService.setViewAs(role);
    this.closeViewAs();
    const routeByRole: Record<string, string> = {
      manager: '/overview',
      sales: '/overview',
      support: '/devices',
      installer: '/installer',
      kunde: '/customer',
    };
    const route = routeByRole[role];
    if (route) {
      void this.router.navigate([route]);
    }
  }

  clearViewAs(): void {
    this.authService.setViewAs(null);
    this.closeViewAs();
  }

  async enablePushNotifications(): Promise<void> {
    this.pushPromptLoading.set(true);
    this.pushPromptError.set(null);
    const result = await this.pushNotificationService.enablePushFromUserGesture();
    this.pushPromptLoading.set(false);
    this.refreshPushPrompt();
    if (result === 'granted' || result === 'denied') {
      return;
    }
    if (result === 'no-vapid') {
      this.pushPromptError.set('Backend: VAPID-Schlüssel fehlen (VAPID_PUBLIC_KEY / VAPID_PRIVATE_KEY).');
      return;
    }
    this.pushPromptError.set('Push konnte nicht aktiviert werden.');
  }

  dismissPushPrompt(): void {
    this.pushNotificationService.dismissPushPrompt();
    this.refreshPushPrompt();
  }
}

